import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const fmt = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-UY')
const pct = (n, dec = 2) => Number(n || 0).toFixed(dec) + '%'

export default function ComisionesHandy() {
  const [planes, setPlanes] = useState([])
  const [config, setConfig] = useState(null)
  const [reales, setReales] = useState([])
  const [loading, setLoading] = useState(true)
  const [editandoConfig, setEditandoConfig] = useState(false)
  const [editandoPlan, setEditandoPlan] = useState(null) // codigo del plan
  const [formConfig, setFormConfig] = useState({})
  const [formPlan, setFormPlan] = useState({})

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    const [pl, cf, rl] = await Promise.all([
      supabase.from('comisiones_handy_planes').select('*').eq('activo', true).order('codigo'),
      supabase.from('comisiones_handy_config').select('*').eq('id', 1).maybeSingle(),
      supabase.from('handy_comisiones_reales').select('*').order('fecha_transaccion', { ascending: false }).limit(20),
    ])
    setPlanes(pl.data || [])
    setConfig(cf.data || null)
    setReales(rl.data || [])
    setLoading(false)
  }

  const planActual = useMemo(() => planes.find(p => p.codigo === config?.plan_actual), [planes, config])

  const ponderado = useMemo(() => {
    if (!config || !planActual) return 0
    return (
      config.mix_debito_pct * planActual.debito_pct +
      config.mix_credito_contado_pct * planActual.credito_contado_pct +
      config.mix_otros_pct * config.otros_pct_estimado
    ) / 100
  }, [config, planActual])

  async function guardarConfig() {
    try {
      const suma = Number(formConfig.mix_debito_pct) + Number(formConfig.mix_credito_contado_pct) + Number(formConfig.mix_otros_pct)
      if (Math.abs(suma - 100) > 0.01) { alert(`El mix debe sumar 100% (suma actual: ${suma}%)`); return }
      await supabase.from('comisiones_handy_config').update({
        plan_actual: formConfig.plan_actual,
        mix_debito_pct: Number(formConfig.mix_debito_pct),
        mix_credito_contado_pct: Number(formConfig.mix_credito_contado_pct),
        mix_otros_pct: Number(formConfig.mix_otros_pct),
        otros_pct_estimado: Number(formConfig.otros_pct_estimado),
        costo_fijo_tx: Number(formConfig.costo_fijo_tx),
        costo_fijo_se_traslada_cliente: !!formConfig.costo_fijo_se_traslada_cliente,
        vigente_desde: formConfig.vigente_desde,
        notas: formConfig.notas,
      }).eq('id', 1)
      setEditandoConfig(false)
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
  }

  async function guardarPlan() {
    try {
      await supabase.from('comisiones_handy_planes').update({
        debito_pct: Number(formPlan.debito_pct),
        credito_contado_pct: Number(formPlan.credito_contado_pct),
        credito_cuotas_min_pct: Number(formPlan.credito_cuotas_min_pct),
        credito_cuotas_max_pct: Number(formPlan.credito_cuotas_max_pct),
        pago_despues_min_pct: Number(formPlan.pago_despues_min_pct),
        pago_despues_max_pct: Number(formPlan.pago_despues_max_pct),
        redpagos_pct: Number(formPlan.redpagos_pct),
        sistarbanc_pct: Number(formPlan.sistarbanc_pct),
        tarifa_negociada: !!formPlan.tarifa_negociada,
        negociacion_notas: formPlan.negociacion_notas,
        negociacion_at: formPlan.tarifa_negociada ? new Date().toISOString() : null,
        notas: formPlan.notas,
      }).eq('codigo', editandoPlan)
      setEditandoPlan(null)
      cargar()
    } catch (e) { alert('Error: ' + e.message) }
  }

  function abrirEditarConfig() { setFormConfig({ ...config }); setEditandoConfig(true) }
  function abrirEditarPlan(p) { setFormPlan({ ...p }); setEditandoPlan(p.codigo) }

  return (
    <AdminLayout>
      <div className="ch-wrap">
        <div className="ch-headline">
          <div>
            <h1>Comisiones Handy</h1>
            <p>Planes oficiales + plan activo + tarifa negociada. Todo editable según condiciones reales.</p>
          </div>
        </div>

        {/* Panel de plan activo */}
        {loading ? <div className="ch-loading">Cargando…</div> : (
          <>
            <div className="ch-actual">
              <div className="ch-actual-head">
                <div>
                  <div className="ch-actual-eyebrow">Plan activo</div>
                  <h2>{planActual?.nombre || '—'}</h2>
                  <div className="ch-actual-sub">Acreditación: <b>{planActual?.acreditacion_label}</b></div>
                </div>
                <button className="ch-btn-primary" onClick={abrirEditarConfig}>Editar configuración</button>
              </div>

              <div className="ch-kpis">
                <KpiBig label="Ponderado actual" value={pct(ponderado, 3)} sub="aplicado a ingresos Handy" color="#dc2626" />
                <KpiBig label="Costo fijo por tx" value={fmt(config?.costo_fijo_tx)} sub={config?.costo_fijo_se_traslada_cliente ? 'trasladado al precio' : 'absorbido en margen'} color="#7c3aed" />
                <KpiBig label="Vigente desde" value={config?.vigente_desde || '—'} sub="" color="#1e40af" />
              </div>

              {/* Mix asumido */}
              <h3 className="ch-h3">Mix asumido de métodos de pago</h3>
              <div className="ch-mix">
                <MixRow label="Débito"        pct={config?.mix_debito_pct}          tasa={planActual?.debito_pct} />
                <MixRow label="Crédito contado" pct={config?.mix_credito_contado_pct} tasa={planActual?.credito_contado_pct} />
                <MixRow label="Otros (Sistarbanc/Redpagos/Pago Después)" pct={config?.mix_otros_pct} tasa={config?.otros_pct_estimado} />
              </div>

              {config?.notas && (
                <div className="ch-notas"><strong>Notas:</strong> {config.notas}</div>
              )}
            </div>

            {/* Tabla de los 4 planes oficiales */}
            <h2 className="ch-h2">Tarifas por plan</h2>
            <div className="ch-tabla-wrap">
              <table className="ch-tabla">
                <thead>
                  <tr>
                    <th>Plan</th>
                    <th>Acreditación</th>
                    <th style={{ textAlign: 'right' }}>Débito</th>
                    <th style={{ textAlign: 'right' }}>Cred. contado</th>
                    <th style={{ textAlign: 'right' }}>Cred. cuotas</th>
                    <th style={{ textAlign: 'right' }}>Pago Después</th>
                    <th style={{ textAlign: 'right' }}>Redpagos</th>
                    <th style={{ textAlign: 'right' }}>Sistarbanc</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {planes.map(p => {
                    const esActivo = p.codigo === config?.plan_actual
                    return (
                      <tr key={p.codigo} className={esActivo ? 'ch-row-activo' : ''}>
                        <td>
                          <div className="ch-plan-nombre">
                            {esActivo && <span className="ch-pill-activo">✓ Activo</span>}
                            {p.nombre}
                          </div>
                          {p.tarifa_negociada && (
                            <div className="ch-pill-negociada">📝 Tarifa negociada</div>
                          )}
                        </td>
                        <td>{p.acreditacion_label}</td>
                        <td style={{ textAlign: 'right' }}>{pct(p.debito_pct)}</td>
                        <td style={{ textAlign: 'right' }}>{pct(p.credito_contado_pct)}</td>
                        <td style={{ textAlign: 'right' }}>{pct(p.credito_cuotas_min_pct)}{p.credito_cuotas_min_pct !== p.credito_cuotas_max_pct ? `–${pct(p.credito_cuotas_max_pct, 2)}` : ''}</td>
                        <td style={{ textAlign: 'right' }}>{pct(p.pago_despues_min_pct)}{p.pago_despues_min_pct !== p.pago_despues_max_pct ? `–${pct(p.pago_despues_max_pct, 2)}` : ''}</td>
                        <td style={{ textAlign: 'right' }}>{pct(p.redpagos_pct)}</td>
                        <td style={{ textAlign: 'right' }}>{pct(p.sistarbanc_pct)}</td>
                        <td><button className="ch-btn-edit" onClick={() => abrirEditarPlan(p)}>Editar</button></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Comisiones reales (cuando arranquemos a cargar) */}
            <h2 className="ch-h2">Comisiones reales cobradas (extracto Handy)</h2>
            {reales.length === 0 ? (
              <div className="ch-empty-reales">
                <strong>📋 Sin datos cargados todavía.</strong>
                <p>Cuando arranque la operación con Handy real, acá vas a poder cargar las comisiones que efectivamente cobró cada transacción (desde el extracto o panel Handy) para cruzar contra lo estimado y detectar diferencias.</p>
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                  <strong>Cómo se conecta hoy:</strong> manualmente desde el extracto Handy. Cuando confirmen API, lo automatizamos.
                </p>
              </div>
            ) : (
              <div className="ch-tabla-wrap">
                <table className="ch-tabla">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Método</th>
                      <th style={{ textAlign: 'right' }}>Monto</th>
                      <th style={{ textAlign: 'right' }}>Comisión %</th>
                      <th style={{ textAlign: 'right' }}>Comisión $</th>
                      <th style={{ textAlign: 'right' }}>Fijo $</th>
                      <th style={{ textAlign: 'right' }}>Neto acred.</th>
                      <th>Acreditado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reales.map(r => (
                      <tr key={r.id}>
                        <td>{r.fecha_transaccion}</td>
                        <td>{r.metodo_pago}{r.cuotas ? ` ${r.cuotas}c` : ''}</td>
                        <td style={{ textAlign: 'right' }}>{fmt(r.monto_transaccion)}</td>
                        <td style={{ textAlign: 'right' }}>{pct(r.comision_pct_real)}</td>
                        <td style={{ textAlign: 'right', color: '#dc2626' }}>−{fmt(r.comision_monto_real)}</td>
                        <td style={{ textAlign: 'right', color: '#dc2626' }}>−{fmt(r.costo_fijo_real)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 800 }}>{fmt(r.monto_neto_acreditado)}</td>
                        <td>{r.fecha_acreditacion}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Info contextual */}
            <div className="ch-info">
              <strong>📚 Cómo usar esta pantalla</strong>
              <ul>
                <li><b>Editar configuración</b>: cambiá el plan activo, el mix asumido o el costo fijo. El cambio impacta automáticamente Reportes P&L y Modelo Financiero.</li>
                <li><b>Editar plan</b>: ajustá las tasas si Handy actualizó tarifas o si negociamos condiciones especiales. Marcá la casilla "Tarifa negociada" para registrar acuerdos custom.</li>
                <li><b>Comisiones reales</b>: cuando llegue el primer extracto Handy real, cargá las comisiones efectivamente cobradas. Eso te permite cruzar contra lo estimado.</li>
              </ul>
              <strong style={{ marginTop: 8, display: 'block' }}>📊 Fuente de las tasas iniciales</strong>
              <p>Las 4 filas cargadas vienen de la propuesta oficial Handy del 04-jun-2026 (mail de Lucía Regueiro con tabla "Plan Clásico / Plan Catorce / Plan Ya"). Editá libremente cuando se actualicen.</p>
            </div>
          </>
        )}
      </div>

      {/* Modal editar config */}
      {editandoConfig && (
        <ModalEditor titulo="Configuración general" onClose={() => setEditandoConfig(false)} onSave={guardarConfig}>
          <Field label="Plan activo">
            <select value={formConfig.plan_actual} onChange={(e) => setFormConfig({ ...formConfig, plan_actual: e.target.value })}>
              {planes.map(p => <option key={p.codigo} value={p.codigo}>{p.nombre}</option>)}
            </select>
          </Field>
          <FormRow>
            <Field label="Mix débito %">
              <input type="number" step="0.01" value={formConfig.mix_debito_pct} onChange={(e) => setFormConfig({ ...formConfig, mix_debito_pct: e.target.value })} />
            </Field>
            <Field label="Mix crédito contado %">
              <input type="number" step="0.01" value={formConfig.mix_credito_contado_pct} onChange={(e) => setFormConfig({ ...formConfig, mix_credito_contado_pct: e.target.value })} />
            </Field>
            <Field label="Mix otros %">
              <input type="number" step="0.01" value={formConfig.mix_otros_pct} onChange={(e) => setFormConfig({ ...formConfig, mix_otros_pct: e.target.value })} />
            </Field>
          </FormRow>
          <FormRow>
            <Field label="Tasa estimada de 'otros' %">
              <input type="number" step="0.01" value={formConfig.otros_pct_estimado} onChange={(e) => setFormConfig({ ...formConfig, otros_pct_estimado: e.target.value })} />
            </Field>
            <Field label="Costo fijo por tx ($)">
              <input type="number" step="0.01" value={formConfig.costo_fijo_tx} onChange={(e) => setFormConfig({ ...formConfig, costo_fijo_tx: e.target.value })} />
            </Field>
            <Field label="Vigente desde">
              <input type="date" value={formConfig.vigente_desde} onChange={(e) => setFormConfig({ ...formConfig, vigente_desde: e.target.value })} />
            </Field>
          </FormRow>
          <Field label="">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!formConfig.costo_fijo_se_traslada_cliente} onChange={(e) => setFormConfig({ ...formConfig, costo_fijo_se_traslada_cliente: e.target.checked })} />
              Costo fijo se traslada al cliente (no absorbido)
            </label>
          </Field>
          <Field label="Notas">
            <textarea rows={3} value={formConfig.notas || ''} onChange={(e) => setFormConfig({ ...formConfig, notas: e.target.value })} />
          </Field>
        </ModalEditor>
      )}

      {/* Modal editar plan */}
      {editandoPlan && (
        <ModalEditor titulo={`Editar tarifas: ${formPlan.nombre}`} onClose={() => setEditandoPlan(null)} onSave={guardarPlan}>
          <FormRow>
            <Field label="Débito %"><input type="number" step="0.001" value={formPlan.debito_pct} onChange={(e) => setFormPlan({ ...formPlan, debito_pct: e.target.value })} /></Field>
            <Field label="Cred. contado %"><input type="number" step="0.001" value={formPlan.credito_contado_pct} onChange={(e) => setFormPlan({ ...formPlan, credito_contado_pct: e.target.value })} /></Field>
          </FormRow>
          <FormRow>
            <Field label="Cred. cuotas min %"><input type="number" step="0.001" value={formPlan.credito_cuotas_min_pct} onChange={(e) => setFormPlan({ ...formPlan, credito_cuotas_min_pct: e.target.value })} /></Field>
            <Field label="Cred. cuotas max %"><input type="number" step="0.001" value={formPlan.credito_cuotas_max_pct} onChange={(e) => setFormPlan({ ...formPlan, credito_cuotas_max_pct: e.target.value })} /></Field>
          </FormRow>
          <FormRow>
            <Field label="Pago Después min %"><input type="number" step="0.001" value={formPlan.pago_despues_min_pct} onChange={(e) => setFormPlan({ ...formPlan, pago_despues_min_pct: e.target.value })} /></Field>
            <Field label="Pago Después max %"><input type="number" step="0.001" value={formPlan.pago_despues_max_pct} onChange={(e) => setFormPlan({ ...formPlan, pago_despues_max_pct: e.target.value })} /></Field>
          </FormRow>
          <FormRow>
            <Field label="Redpagos %"><input type="number" step="0.001" value={formPlan.redpagos_pct} onChange={(e) => setFormPlan({ ...formPlan, redpagos_pct: e.target.value })} /></Field>
            <Field label="Sistarbanc %"><input type="number" step="0.001" value={formPlan.sistarbanc_pct} onChange={(e) => setFormPlan({ ...formPlan, sistarbanc_pct: e.target.value })} /></Field>
          </FormRow>
          <Field label="">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!formPlan.tarifa_negociada} onChange={(e) => setFormPlan({ ...formPlan, tarifa_negociada: e.target.checked })} />
              📝 Tarifa negociada (diferente a la pública del plan)
            </label>
          </Field>
          {formPlan.tarifa_negociada && (
            <Field label="Notas de la negociación">
              <textarea rows={3} value={formPlan.negociacion_notas || ''} onChange={(e) => setFormPlan({ ...formPlan, negociacion_notas: e.target.value })} placeholder="Ej: descuento negociado con Lucía Regueiro el DD-MM, vigente desde…" />
            </Field>
          )}
          <Field label="Notas generales">
            <textarea rows={2} value={formPlan.notas || ''} onChange={(e) => setFormPlan({ ...formPlan, notas: e.target.value })} />
          </Field>
        </ModalEditor>
      )}

      <style>{CSS}</style>
    </AdminLayout>
  )
}

function KpiBig({ label, value, sub, color }) {
  return (
    <div className="ch-kpi">
      <div className="ch-kpi-l">{label}</div>
      <div className="ch-kpi-v" style={{ color }}>{value}</div>
      {sub && <div className="ch-kpi-s">{sub}</div>}
    </div>
  )
}

function MixRow({ label, pct: p, tasa }) {
  return (
    <div className="ch-mix-row">
      <div className="ch-mix-label">{label}</div>
      <div className="ch-mix-bar"><div className="ch-mix-bar-fill" style={{ width: `${p}%` }} /></div>
      <div className="ch-mix-pct">{p}%</div>
      <div className="ch-mix-tasa">→ {Number(tasa).toFixed(3)}% comisión</div>
    </div>
  )
}

function ModalEditor({ titulo, onClose, onSave, children }) {
  return (
    <div className="ch-modal-bg" onClick={onClose}>
      <div className="ch-modal" onClick={(e) => e.stopPropagation()}>
        <button className="ch-modal-close" onClick={onClose}>×</button>
        <h2>{titulo}</h2>
        <div>{children}</div>
        <div className="ch-modal-actions">
          <button className="ch-btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="ch-btn-primary" onClick={onSave}>Guardar</button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return <label className="ch-field"><span>{label}</span>{children}</label>
}
function FormRow({ children }) {
  return <div className="ch-form-row">{children}</div>
}

const CSS = `
.ch-wrap { padding: 16px 20px 32px; max-width: 1400px; margin: 0 auto }
.ch-headline { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; flex-wrap: wrap; margin-bottom: 18px }
.ch-headline h1 { font-size: 26px; font-weight: 900; color: #1A3461; margin: 0 0 4px; letter-spacing: -.02em }
.ch-headline p { font-size: 13px; color: #64748b; margin: 0 }
.ch-loading { padding: 60px; text-align: center; color: #94a3b8 }

.ch-actual { background: linear-gradient(135deg, #1A3461, #0f2147); color: #fff; padding: 22px 26px; border-radius: 14px; margin-bottom: 24px }
.ch-actual-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; flex-wrap: wrap; margin-bottom: 18px }
.ch-actual-eyebrow { font-size: 11px; opacity: .7; text-transform: uppercase; font-weight: 800; letter-spacing: .08em }
.ch-actual-head h2 { margin: 4px 0; font-size: 24px; font-weight: 900; letter-spacing: -.02em }
.ch-actual-sub { font-size: 13px; opacity: .85 }
.ch-h3 { font-size: 13px; font-weight: 800; opacity: .9; margin: 18px 0 10px; text-transform: uppercase; letter-spacing: .04em }

.ch-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 10px }
.ch-kpi { background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.12); border-radius: 10px; padding: 12px 14px }
.ch-kpi-l { font-size: 10.5px; opacity: .7; text-transform: uppercase; font-weight: 800; letter-spacing: .04em }
.ch-kpi-v { font-size: 24px; font-weight: 900; margin-top: 2px }
.ch-kpi-s { font-size: 11px; opacity: .7; margin-top: 2px }

.ch-mix { display: flex; flex-direction: column; gap: 6px; background: rgba(255,255,255,.05); padding: 12px; border-radius: 8px }
.ch-mix-row { display: grid; grid-template-columns: 220px 1fr 60px 180px; gap: 12px; align-items: center; font-size: 13px }
.ch-mix-label { font-weight: 700 }
.ch-mix-bar { height: 6px; background: rgba(255,255,255,.1); border-radius: 999px; overflow: hidden }
.ch-mix-bar-fill { height: 100%; background: linear-gradient(90deg, #00d4e0, #00A8B5); border-radius: 999px }
.ch-mix-pct { text-align: right; font-weight: 800 }
.ch-mix-tasa { font-size: 12px; opacity: .8 }

.ch-notas { background: rgba(255,255,255,.05); padding: 10px 14px; border-radius: 8px; font-size: 12.5px; line-height: 1.5; margin-top: 12px }
.ch-notas strong { font-weight: 800; color: #00d4e0 }

.ch-h2 { font-size: 16px; font-weight: 900; color: #1A3461; margin: 24px 0 12px }

.ch-tabla-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 12px; overflow: auto }
.ch-tabla { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 900px }
.ch-tabla th { text-align: left; padding: 10px 12px; background: #f8fafc; font-weight: 700; color: #475569; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; border-bottom: 1px solid #e2e8f0 }
.ch-tabla td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; color: #1A3461 }
.ch-row-activo { background: #ecfeff }
.ch-plan-nombre { font-weight: 800; display: flex; align-items: center; gap: 8px; flex-wrap: wrap }
.ch-pill-activo { background: #00A8B5; color: #fff; padding: 2px 7px; border-radius: 4px; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .04em }
.ch-pill-negociada { display: inline-block; margin-top: 4px; background: #fef3c7; color: #92400e; padding: 2px 7px; border-radius: 4px; font-size: 10px; font-weight: 800 }

.ch-btn-primary { background: #00A8B5; color: #fff; border: 0; padding: 9px 18px; border-radius: 8px; font-size: 13px; font-weight: 800; cursor: pointer; font-family: inherit }
.ch-btn-primary:hover { background: #008996 }
.ch-btn-secondary { background: #fff; border: 1px solid #cbd5e1; color: #475569; padding: 9px 18px; border-radius: 8px; font-size: 13px; font-weight: 700; cursor: pointer; font-family: inherit }
.ch-btn-edit { background: transparent; border: 1px solid #cbd5e1; color: #475569; padding: 3px 9px; border-radius: 5px; font-size: 11px; font-weight: 700; cursor: pointer }
.ch-btn-edit:hover { background: #f1f5f9 }

.ch-empty-reales { background: #fff; border: 1px dashed #cbd5e1; border-radius: 12px; padding: 20px 24px; text-align: left }
.ch-empty-reales strong { display: block; color: #1A3461; font-size: 14px; margin-bottom: 6px }
.ch-empty-reales p { font-size: 13px; color: #475569; margin: 4px 0; line-height: 1.5 }

.ch-info { margin-top: 22px; padding: 14px 18px; background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; font-size: 12.5px; color: #1e3a8a; line-height: 1.6 }
.ch-info strong { font-size: 13px; color: #1e3a8a }
.ch-info ul { margin: 8px 0; padding-left: 22px }
.ch-info li { margin-bottom: 4px }
.ch-info b { color: #1e40af }

/* Modal */
.ch-modal-bg { position: fixed; inset: 0; background: rgba(15,31,61,.55); z-index: 100; display: flex; align-items: center; justify-content: center; padding: 16px }
.ch-modal { background: #fff; border-radius: 14px; max-width: 700px; width: 100%; max-height: 92vh; overflow-y: auto; padding: 26px 28px; position: relative }
.ch-modal h2 { margin: 0 0 18px; font-size: 19px; color: #1A3461; font-weight: 900 }
.ch-modal-close { position: absolute; top: 12px; right: 12px; background: none; border: 0; font-size: 22px; cursor: pointer; color: #64748b }
.ch-modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 14px; padding-top: 14px; border-top: 1px solid #e2e8f0 }

.ch-form-row { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px }
@media (max-width: 640px) { .ch-form-row { grid-template-columns: 1fr } }
.ch-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px }
.ch-field > span { font-size: 12px; font-weight: 700; color: #475569 }
.ch-field input, .ch-field select, .ch-field textarea { padding: 9px 11px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; font-family: inherit; color: #1A3461 }
.ch-field input:focus, .ch-field select:focus, .ch-field textarea:focus { outline: 0; border-color: #00A8B5; box-shadow: 0 0 0 3px rgba(0,168,181,.12) }

@media (max-width: 720px) {
  .ch-mix-row { grid-template-columns: 1fr 60px; gap: 6px }
  .ch-mix-bar, .ch-mix-tasa { display: none }
}
`
